import React, { useReducer, useState } from "react";
import { useTranslation } from "react-i18next";

import { ConfirmDialog } from "@devices/components/shared";
import { MdmPolicyCommand } from "@devices/types";
import { Draggable, DraggableProvided, DraggableStateSnapshot } from "@hello-pangea/dnd";
import { DialogContentText } from "@mui/material";

import { EditPolicyCommandDialog } from "./EditPolicyCommandDialog";
import { PolicyCommandDetails } from "./PolicyCommandDetails";
import { getPolicyRuleLabels } from "./policyCommandDisplay";
import { PolicyCommandRow } from "./PolicyCommandRow";

interface PolicyCommandItemProps {
    policyCommand: MdmPolicyCommand;
    content: MdmPolicyCommand[];
    index: number;
    onUpdate?: (content: MdmPolicyCommand[]) => Promise<boolean>;
    readOnly?: boolean;
    loading?: boolean;
}

const PolicyCommandItem = ({
    policyCommand,
    content,
    index,
    onUpdate,
    readOnly = false,
    loading = false,
}: PolicyCommandItemProps) => {
    const [editDialogOpen, setEditDialogOpen] = useReducer((state) => !state, false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [open, setOpen] = useState(-1);

    const { t } = useTranslation(["modals"]);

    const commandLabels = getPolicyRuleLabels(policyCommand);

    const toggleModal = () => setIsDeleteModalOpen((prev) => !prev);
    const toggleOpen = () => setOpen(open === index ? -1 : index);

    const handleDelete = async (policy: MdmPolicyCommand) => {
        if (!onUpdate) {
            return;
        }
        const filteredContent = content.filter((c) => c !== policy);
        await onUpdate(filteredContent);
    };

    if (readOnly) {
        return (
            <React.Fragment>
                <PolicyCommandRow
                    index={index}
                    commandLabels={commandLabels}
                    open={open}
                    onToggleOpen={toggleOpen}
                    readOnly={readOnly}
                    loading={loading}
                    onEdit={setEditDialogOpen}
                    onDeleteClick={() => setIsDeleteModalOpen(true)}
                />
                <PolicyCommandDetails policyCommand={policyCommand} index={index} open={open} />

                {onUpdate ? (
                    <EditPolicyCommandDialog
                        content={content}
                        onUpdate={onUpdate}
                        policyCommand={policyCommand}
                        isOpen={editDialogOpen}
                        toggleDialog={setEditDialogOpen}
                    />
                ) : null}
            </React.Fragment>
        );
    }

    return (
        <Draggable draggableId={index.toString()} index={index} isDragDisabled={loading}>
            {(draggableProvided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                <React.Fragment>
                    <PolicyCommandRow
                        index={index}
                        commandLabels={commandLabels}
                        open={open}
                        onToggleOpen={toggleOpen}
                        readOnly={readOnly}
                        loading={loading}
                        onEdit={setEditDialogOpen}
                        onDeleteClick={() => setIsDeleteModalOpen(true)}
                        draggableProvided={draggableProvided}
                        snapshot={snapshot}
                    />
                    <PolicyCommandDetails policyCommand={policyCommand} index={index} open={open} />

                    {onUpdate ? (
                        <EditPolicyCommandDialog
                            content={content}
                            onUpdate={onUpdate}
                            policyCommand={policyCommand}
                            isOpen={editDialogOpen}
                            toggleDialog={setEditDialogOpen}
                        />
                    ) : null}

                    <ConfirmDialog
                        title={t("modals:deleteRule.title")}
                        dangerButton
                        primaryActionText={t("modals:deleteRule.primaryActionText")}
                        open={isDeleteModalOpen}
                        onConfirm={() => handleDelete(policyCommand)}
                        onClose={toggleModal}
                    >
                        <DialogContentText>{t("modals:deleteRule.description")}</DialogContentText>
                    </ConfirmDialog>
                </React.Fragment>
            )}
        </Draggable>
    );
};

export default PolicyCommandItem;
